import { ComponentChildren } from 'preact';
import { useState, useRef } from 'preact/hooks';
import { useIntl } from 'shared/intl/use-intl';
import { rpx } from 'client/lib/rpx-client';
import { detectTimezone } from 'shared/dateutil';
import { showError } from '@components/app-error';
import { CheckoutHeader } from './checkout-page';
import { IcoLock, IcoMail, IcoUser, IcoX } from '@components/icons';
import { Button, BtnPrimary } from '@components/buttons';
import { FieldError, FormGroup, InputLabel, AsyncForm } from '@components/async-form';
import { Password } from '@components/password';
import { StripeContext, StripeInput } from './stripe';
import { processStripePayment, PayWhatYouCanInput } from './stripe-checkout-form';
import { describeCheckoutError, loggableCheckoutError } from './checkout-logic';
import { reportCheckoutError } from './report-checkout-error';
import { TrustStrip } from './trust-strip';
import { CurrentUser, useAuth } from 'client/lib/auth';
import { CouponRow, PriceRow } from 'server/types';
import * as pmtmath from 'shared/payments/math';
import { CheckoutFn, PurchaseFn, StripeHandlerFn } from 'shared/payments/types';
import { ERROR_CODES } from 'shared/consts';
import { ForgotPasswordFooter } from './forgot-password-footer';

type Props = {
  price: PriceRow;
  coupon?: CouponRow;
  collectAddress: boolean;
  isGift?: boolean;
  giftingToEmail?: string;
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(opts?: { newUser?: boolean }): Promise<unknown>;
  subtitle?: string;
  footer?: ComponentChildren;
};

function IconInputWrapper({
  icon,
  children,
}: {
  icon: ComponentChildren;
  children: ComponentChildren;
}) {
  return (
    <label class="relative flex">
      <span class="absolute flex items-center justify-center left-px top-px bottom-px w-10 bg-gray-50 rounded-l border-r">
        {icon}
      </span>
      {children}
    </label>
  );
}

type Mode = 'init' | 'existingUser';

export function CombinedCheckoutForm({
  price,
  coupon,
  collectAddress,
  isGift,
  handleStripe,
  beginPurchase,
  onPurchaseComplete,
  subtitle,
  footer,
}: Props) {
  const intl = useIntl();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mode, setMode] = useState<Mode>('init');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ctx, setCtx] = useState<StripeContext | undefined>(undefined);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const [customAmount, setCustomAmount] = useState(price.priceInCents);
  const checkedEmailRef = useRef('');

  const isPwyc = price.payWhatYouCan;
  const freeTrialPeriod = pmtmath.freeTrialPeriod({ price, coupon });
  const isFree = pmtmath.isFree({ price, coupon });
  const paymentRequired = !freeTrialPeriod && !!pmtmath.initialPrice({ price, coupon });

  let actionText: string;
  if (isFree) {
    actionText = intl('Enroll Now →');
  } else if (!paymentRequired && freeTrialPeriod) {
    actionText = intl('Start Free Trial →');
  } else if (isGift) {
    actionText = intl('Purchase Gift →');
  } else {
    actionText = intl('Buy Now →');
  }

  const checkExistingUser = async (emailToCheck: string) => {
    if (!emailToCheck || checkedEmailRef.current === emailToCheck) {
      return;
    }
    checkedEmailRef.current = emailToCheck;
    try {
      const existingUser = await rpx.courses.getIsMember({ email: emailToCheck });
      if (existingUser) {
        setMode('existingUser');
      } else {
        setMode('init');
      }
    } catch {
      setMode('init');
    }
  };

  const processPaymentWithUser = async (
    user: NonNullable<CurrentUser>,
    opts?: { newUser?: boolean },
  ) => {
    if (isFree) {
      await beginPurchase({});
      await onPurchaseComplete(opts);
      return;
    }

    if (!ctx) {
      showError(new Error(intl('Payment is not ready. Please try again.')));
      return;
    }

    const checkout: CheckoutFn = async ({ priceId, couponId }) => {
      const result = await processStripePayment({
        ctx,
        user,
        priceId,
        couponId,
        customAmountInCents: isPwyc ? customAmount : undefined,
        processPayment: beginPurchase,
      });
      return !!result && result.type !== 'validationError';
    };

    const success = handleStripe
      ? await handleStripe({ checkout })
      : await checkout({ priceId: price.id, couponId: coupon?.id });

    if (!success) {
      return;
    }
    await onPurchaseComplete(opts);
  };

  const handleSubmit = async (formData: any) => {
    try {
      setIsLoading(true);
      setCardError(undefined);

      if (mode === 'existingUser') {
        const user = await rpx.auth.login({
          email,
          password: formData.password,
        });
        const sessionUser = await auth.login(
          Promise.resolve({ ...user, level: 'student' as const }),
        );
        await processPaymentWithUser(sessionUser);
        return;
      }

      try {
        const user = await rpx.auth.registerNewUserForCheckout({
          name: formData.name || name,
          email,
          timezone: detectTimezone(),
        });
        const sessionUser = await auth.login(
          Promise.resolve({ ...user, level: 'student' as const }),
        );
        await processPaymentWithUser(sessionUser, { newUser: true });
      } catch (err: any) {
        if (err?.data?.code === ERROR_CODES.userExists) {
          setMode('existingUser');
          return;
        }
        throw err;
      }
    } catch (err) {
      const described = describeCheckoutError(err);
      reportCheckoutError({ step: 'checkout', priceId: price.id, err });
      if (described.kind === 'card') {
        setCardError(described.message);
        console.warn('checkout card error', loggableCheckoutError(err));
      } else {
        showError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CheckoutHeader
        title={mode === 'existingUser' ? intl('Welcome Back!') : intl('Complete Your Purchase')}
        subtitle={
          mode === 'existingUser'
            ? intl('We found an existing account for this email. Sign in to continue.')
            : subtitle
        }
      />
      <AsyncForm class="flex flex-col gap-4" onSubmit={handleSubmit}>
        {mode === 'existingUser' ? (
          <div>
            <InputLabel>{intl('Email')}</InputLabel>
            <Button
              class="flex gap-2 text-left w-full p-0 ruz-input bg-gray-50 rounded-sm border items-center relative pl-12"
              onClick={() => {
                setMode('init');
                checkedEmailRef.current = '';
              }}
            >
              <span class="flex absolute inset-y-0 left-0 items-center justify-center w-10 border-r">
                <IcoMail />
              </span>
              <span class="grow flex items-center p-2 py-1.5">{email}</span>
              <span class="px-2.5 flex items-center justify-center">
                <IcoX class="size-4 opacity-75" />
              </span>
            </Button>
          </div>
        ) : (
          <>
            <FormGroup prop="email" class="w-full">
              <InputLabel>{intl('Email')}</InputLabel>
              <IconInputWrapper icon={<IcoMail />}>
                <input
                  type="email"
                  required
                  autoFocus
                  placeholder={intl('Your email address')}
                  name="email"
                  class="ruz-input block w-full sm:text-sm sm:leading-5 bg-transparent pl-14"
                  data-private
                  value={email}
                  onInput={(e: any) => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                  onBlur={() => checkExistingUser(email)}
                />
              </IconInputWrapper>
              {emailError && <FieldError>{emailError}</FieldError>}
            </FormGroup>
            <FormGroup prop="name" class="w-full">
              <InputLabel>{intl('Name')}</InputLabel>
              <IconInputWrapper icon={<IcoUser />}>
                <input
                  type="text"
                  name="name"
                  class="ruz-input pl-14"
                  placeholder={intl('Your name')}
                  required
                  value={name}
                  onInput={(e: any) => setName(e.target.value)}
                />
              </IconInputWrapper>
            </FormGroup>
          </>
        )}

        {mode === 'existingUser' && (
          <FormGroup prop="password" class="animate-slide-bottom">
            <InputLabel>{intl('Password')}</InputLabel>
            <IconInputWrapper icon={<IcoLock />}>
              <Password
                name="password"
                placeholder={intl('Password')}
                autocomplete="current-password"
                autoFocus
                wrapperClass="w-full"
                class="bg-transparent pl-14"
              />
            </IconInputWrapper>
            <ForgotPasswordFooter email={email} />
          </FormGroup>
        )}

        {!isFree && mode === 'existingUser' && (
          <div class="pt-2">
            <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {intl('Payment Details')}
            </p>
          </div>
        )}

        {isPwyc && (
          <PayWhatYouCanInput price={price} value={customAmount} onChange={setCustomAmount} />
        )}

        {!isFree && (
          <FormGroup prop="paymentMethod">
            <InputLabel>{intl('Payment')}</InputLabel>
            <StripeInput
              customerName={name}
              priceId={price.id}
              onReady={setCtx}
              collectAddress={collectAddress}
              disabled={isLoading}
              amount={isPwyc ? customAmount : pmtmath.initialPrice({ price, coupon })}
              currency={price.currency}
              mode={
                freeTrialPeriod
                  ? 'setup'
                  : price.paymentType === 'subscription' || price.paymentType === 'paymentplan'
                    ? 'subscription'
                    : 'payment'
              }
            />
            {cardError && (
              <div
                role="alert"
                class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {cardError}
              </div>
            )}
          </FormGroup>
        )}

        <div class="flex flex-col">
          <BtnPrimary class="rounded-md py-3 font-semibold w-full" isLoading={isLoading}>
            {actionText}
          </BtnPrimary>
        </div>

        {!isFree && <TrustStrip />}

        {!isFree && !paymentRequired && (
          <p class="text-gray-600 text-center">
            {!!freeTrialPeriod &&
              intl('You will not be charged until the end of the free trial period.')}
            {!freeTrialPeriod && intl('You will not be charged today.')}
          </p>
        )}

        {footer}
      </AsyncForm>
    </>
  );
}
